import { useState } from "react";
import {
   Grid, Text, Badge, Group, Stack,
   Progress, Paper, Box, Center, Flex, SimpleGrid,
   Tabs, Switch, Loader,
   Divider,
} from "@mantine/core";
import {
   AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
   ResponsiveContainer, Cell, PieChart, Pie,
} from "recharts";
import useAdministrativeDashboard from '../../../../hooks/Administrative/Dashboard/useAdministrativeDashboard';
import { DatePickerInput } from "@mantine/dates";
import moment from "moment/moment";
import { IconCoin, IconReceiptTax, IconSchool, IconTrendingUp, IconUsers } from "@tabler/icons-react";
import { formatCourseStartDate } from "../../../../utils/courses-dates";
const TEAL = "#2d6a6a";   
const TEAL_BG = "#eaf3f3";   

const INK = "var(--color-text-secondary";   
const SUBTEXT = "var(--color-text-primary)";  
const MUTED = "var(--color-text-secondary)";  

const SURFACE = "#f4f5f7";  
const CARD = "#ffffff";  
const BORDER = "#e2e6ea";   

const RED_SOFT = "#c0392b";   
const GREEN_SOFT = "#2e7d5e"; 

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];


// ── Helpers ───────────────────────────────────────────────────────────────
const fmt = (n, currency) =>
   currency === "USD" ? `$${n.toLocaleString("en-US")}` : `$${n.toLocaleString("es-AR")}`;

const SectionTitle = ({ children }) => (
   <Text
      fw={600}
      size="xs"
      tt="uppercase"
      c={MUTED}
      mb="sm"
      style={{ letterSpacing: 1.5 }}
   >
      {children}
   </Text>
);

const RowDivider = () => (
   <Box style={{ height: 1, background: BORDER }} />
);

const ACCENT_CONFIG = {
   teal: {
      border: "#2a9d8f",
      iconColor: "#2a9d8f",
      iconBg: "linear-gradient(135deg, #e6f7f5 0%, #c8ede9 100%)",
      valueShadow: "rgba(42,157,143,0.15)",
   },
   red: {
      border: "#e05a5a",
      iconColor: "#e05a5a",
      iconBg: "linear-gradient(135deg, #fdf0f0 0%, #fad9d9 100%)",
      valueShadow: "rgba(224,90,90,0.12)",
   },
   green: {
      border: "#3a9e5f",
      iconColor: "#3a9e5f",
      iconBg: "linear-gradient(135deg, #eaf7ef 0%, #c8ebd6 100%)",
      valueShadow: "rgba(58,158,95,0.12)",
   },
   blue: {
      border: "#3b82c4",
      iconColor: "#3b82c4",
      iconBg: "linear-gradient(135deg, #eaf2fb 0%, #c8ddf5 100%)",
      valueShadow: "rgba(59,130,196,0.12)",
   },
   amber: {
      border: "#d97706",
      iconColor: "#d97706",
      iconBg: "linear-gradient(135deg, #fef9ec 0%, #fde9b4 100%)",
      valueShadow: "rgba(217,119,6,0.12)",
   },
};


const KpiCard = ({ label, value, sub, accent = "teal", icon }) => {
   const cfg = ACCENT_CONFIG[accent] ?? ACCENT_CONFIG.teal;

   return (
      <Paper
         p="xl"
         radius="lg"
         style={{
            background: CARD,
            border: `1px solid ${BORDER}`,
            borderLeft: `3px solid ${cfg.border}`,
            boxShadow: `0 2px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)`,
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
            cursor: "default",
            position: "relative",
            overflow: "hidden",
         }}
         onMouseEnter={e => {
            e.currentTarget.style.boxShadow = `0 8px 28px rgba(0,0,0,0.10), 0 2px 8px ${cfg.valueShadow}`;
            e.currentTarget.style.transform = "translateY(-2px)";
         }}
         onMouseLeave={e => {
            e.currentTarget.style.boxShadow = `0 2px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)`;
            e.currentTarget.style.transform = "translateY(0)";
         }}
      >
         <Box
            style={{
               position: "absolute",
               top: -28,
               right: -28,
               width: 90,
               height: 90,
               borderRadius: "50%",
               background: cfg.iconBg,
               opacity: 0.5,
               pointerEvents: "none",
            }}
         />

         <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Box style={{ flex: 1, minWidth: 0 }}>
               <Text
                  size="10px"
                  tt="uppercase"
                  fw={700}
                  c={MUTED}
                  mb={10}
                  style={{ letterSpacing: 2 }}
               >
                  {label}
               </Text>

               <Text
                  fw={800}
                  c={INK}
                  style={{
                     fontSize: 30,
                     lineHeight: 1,
                     fontVariantNumeric: "tabular-nums",
                     letterSpacing: "-0.5px",
                  }}
               >
                  {value}
               </Text>

               {sub && (
                  <Text
                     size="xs"
                     c={MUTED}
                     mt={8}
                     fw={500}
                     style={{ letterSpacing: 0.2 }}
                  >
                     {sub}
                  </Text>
               )}
            </Box>

            {icon && (
               <Box
                  style={{
                     flexShrink: 0,
                     width: 44,
                     height: 44,
                     borderRadius: 12,
                     background: cfg.iconBg,
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                     color: cfg.iconColor,
                     border: `1px solid ${cfg.border}22`,
                  }}
               >
                  {icon}
               </Box>
            )}
         </Group>
      </Paper>
   );
};




const TypeBadge = ({ type }) => (
   <Badge
      size="xs"
      style={{
         background: TEAL_BG,
         color: SUBTEXT,
         border: "none",
         fontWeight: 600,
         letterSpacing: 0.3,
      }}
   >
      {type}
   </Badge>
);

const CustomTooltip = ({ active, payload, label, currency }) => {
   if (!active || !payload?.length) return null;
   return (
      <Paper
         p="sm"
         radius="sm"
         style={{
            background: CARD,
            border: `1px solid ${BORDER}`,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
         }}
      >
         <Text size="xs" fw={600} c={SUBTEXT}>{label}</Text>
         <Text size="sm" fw={700} c={INK}>{fmt(payload[0].value, currency)}</Text>
      </Paper>
   );
};

export default function AdminDashboard() {

   const [tab, setTab] = useState("ingresos");
   const [currency, setCurrency] = useState("USD");
   const { data: d, isLoading, dateFilter, setDateFilter } = useAdministrativeDashboard();
   if (isLoading) return (
      <Center py="xl">
         <Loader size="sm" color={TEAL} />
      </Center>
   );


   const ICON_SIZE = 20;
   const ICON_STROKE = 1.8;

   if (!d) return <></>;

   const income = d.income[currency];

   const areaData = MONTHS
      .map((m, i) => ({ month: m, value: income.paymentsPerMonth[i] || 0 }))
      .filter(v => v.value > 0);

   const platformPie = [
      { name: "PayPal", value: income.totalPerPlataform.paypal, color: "#3a6080" },
      { name: "Transferencia", value: income.totalPerPlataform.transferencia, color: TEAL },
      { name: "Pagos360", value: income.totalPerPlataform.pagos360, color: "#8c6d3f" },
   ].filter(p => p.value > 0);

   const platformTotal = platformPie.reduce((a, b) => a + b.value, 0);

   const barData = [
      ...d.courses.threeMostPopularCourses.map(c => ({
         name: c.comercialInformation.name.split(" ").slice(0, 3).join(" "),
         alumnos: c.studentsCount,
         kind: "top",
      })),
      ...d.courses.threeLeastAttendedCourses.map(c => ({
         name: c.comercialInformation.name.split(" ").slice(0, 3).join(" "),
         alumnos: c.studentsCount,
         kind: "low",
      })),
   ];

   return (
      <>
         <Divider mb={20} mt={40} w='100%' label='Estadísticas' />

         <Box mb={40} >

            <Flex justify='center' direction='column' align={'center'} mb='md'>
               <DatePickerInput
                  classNames={{ root: "moderationContainer-DatePicker-root" }}
                  type="range"
                  label="Mostrando datos de:"
                  value={dateFilter}
                  onChange={(value) => setDateFilter(value)}
               />
               <span>
                  * Por defecto se muestran datos del mes actual (desde el 1ro hasta la fecha actual)
               </span>
            </Flex>

            <Switch
               size="lg"
               onLabel="USD"
               offLabel="ARS"
               color="teal"
               label='Moneda'
               my={'md'}
               labelPosition="left"
               styles={{
                  track: { cursor: "pointer" },
                  trackLabel: { fontSize: 10, fontWeight: 700 },
               }}
               checked={currency === "USD"}
               onChange={e => setCurrency(e.currentTarget.checked ? "USD" : "ARS")}
            />

            <SimpleGrid cols={{ base: 2 }} mb="xl">
               <KpiCard
                  label="Capacitaciones totales"
                  value={d.courses.totals}
                  sub="En plataforma"
                  accent="teal"
                  icon={<IconSchool size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
               />
               <KpiCard
                  label="Estudiantes"
                  value={d.students.totals.toLocaleString()}
                  sub="Registrados"
                  accent="blue"
                  icon={<IconUsers size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
               />
            </SimpleGrid>

            <SimpleGrid cols={{ base: 1, sm: 3 }} mb="xl">
               <KpiCard
                  label="Comisiones"
                  sub="Del rango seleccionado"
                  value={fmt(income.fee_amount, currency)}
                  accent="red"
                  icon={<IconReceiptTax size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
               />
               <KpiCard
                  label="Neto"
                  value={fmt(income.net_amount, currency)}
                  sub="Del rango seleccionado"
                  accent="green"
                  icon={<IconTrendingUp size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
               />
               <KpiCard
                  label="Total"
                  value={fmt(income.current_range, currency)}
                  sub="Del rango seleccionado"
                  accent="teal"
                  icon={<IconCoin size={ICON_SIZE} strokeWidth={ICON_STROKE} />}
               />
            </SimpleGrid>

            {/* ── Tabs ── */}
            <Tabs
               value={tab}
               onChange={setTab}
               styles={{
                  tab: {
                     color: MUTED,
                     fontWeight: 700,
                     fontSize: 11,
                     letterSpacing: 1.2,
                     textTransform: "uppercase",
                     padding: "10px 14px",
                     "&[dataActive]": { color: INK },
                  },
                  list: { borderColor: BORDER },
                  panel: { paddingTop: 20 },
               }}
            >
               <Tabs.List>
                  <Tabs.Tab value="ingresos">Ingresos</Tabs.Tab>
                  <Tabs.Tab value="cursos">Cursos</Tabs.Tab>
                  <Tabs.Tab value="inicio">Inicios</Tabs.Tab>
               </Tabs.List>

               <Tabs.Panel value="ingresos">
                  <Grid gutter="md">

                     {/* Area chart */}
                     <Grid.Col span={{ base: 12, md: 8 }}>
                        <Paper p="lg" radius="md" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
                           <Flex justify="space-between" align="center" mb="md">
                              <SectionTitle>Ingresos mensuales · {currency === 'USD' ? 'DOLARES' : "PESOS ARGENTINOS"}</SectionTitle>

                           </Flex>
                           <ResponsiveContainer width="100%" height={220}>
                              {areaData && areaData?.length > 0 ? <AreaChart data={areaData}>
                                 <defs>
                                    <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                                       <stop offset="5%" stopColor={TEAL} stopOpacity={0.15} />
                                       <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
                                    </linearGradient>
                                 </defs>
                                 <XAxis dataKey="month" tick={{ fill: MUTED, fontSize: 11 }} axisLine={false} tickLine={false} />
                                 <YAxis tick={{ fill: MUTED, fontSize: 10 }} axisLine={false} tickLine={false}
                                    tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                                 <Tooltip content={<CustomTooltip currency={currency} />} />
                                 <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke={TEAL}
                                    strokeWidth={2}
                                    fill="url(#areaGrad)"
                                    dot={{ fill: TEAL, r: 3, strokeWidth: 0 }}
                                 />
                              </AreaChart>
                                 : <Text size='sm'
                                 >
                                    No se han encontrado resultados para este rango de fechas del <strong>{moment(dateFilter[0]).format('DD-MM-YYYY')}</strong> al <strong>{moment(dateFilter[1]).format('DD-MM-YYYY')}</strong></Text>
                              }
                           </ResponsiveContainer>
                        </Paper>
                     </Grid.Col>

                     {/* Plataformas */}
                     <Grid.Col span={{ base: 12, md: 4 }}>
                        <Paper p="lg" radius="md" style={{ background: CARD, border: `1px solid ${BORDER}` }} h="100%">
                           <SectionTitle>Por plataforma</SectionTitle>
                           <Center mb="sm">
                              <PieChart width={150} height={150}>
                                 <Pie data={platformPie} cx={70} cy={70} innerRadius={42} outerRadius={65} paddingAngle={2} dataKey="value">
                                    {platformPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                                 </Pie>
                                 <Tooltip
                                    contentStyle={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                                    itemStyle={{ color: INK }}
                                    formatter={v => fmt(v, currency)}
                                 />
                              </PieChart>
                           </Center>
                           <Stack gap={10}>
                              {platformPie.map(p => (
                                 <Box key={p.name}>
                                    <Flex justify="space-between" mb={4}>
                                       <Group gap={6}>
                                          <Box style={{ width: 6, height: 6, borderRadius: 1, background: p.color, flexShrink: 0 }} />
                                          <Text size="xs" c={SUBTEXT}>{p.name}</Text>
                                       </Group>
                                       <Text size="xs" fw={600} c={SUBTEXT}>
                                          {platformTotal ? Math.round((p.value / platformTotal) * 100) : 0}%
                                       </Text>
                                    </Flex>
                                    <Progress
                                       value={platformTotal ? (p.value / platformTotal) * 100 : 0}
                                       size={3}
                                       radius="xl"
                                       bg={SURFACE}
                                       color={p.color === TEAL ? "teal" : "gray"}
                                    />
                                 </Box>
                              ))}
                           </Stack>
                        </Paper>
                     </Grid.Col>

                     {/* Mayor ingreso */}
                     <Grid.Col span={{ base: 12, md: 6 }}>
                        <Paper p="lg" radius="md" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
                           <SectionTitle>Mayor ingreso por curso</SectionTitle>
                           <Stack gap={0}>
                              {(!d?.income?.threeHigherIncomeCourses || d?.income?.threeHigherIncomeCourses?.length === 0) && <Text size="sm"> No se han encontrado resultados para este rango de fechas del <strong>{moment(dateFilter[0]).format('DD-MM-YYYY')}</strong> al <strong>{moment(dateFilter[1]).format('DD-MM-YYYY')}</strong></Text>}


                              {d.income.threeHigherIncomeCourses?.map((c, i) => {
                                 const val = currency === "USD" ? c.totalEarningsUSD : c.totalEarningsARS;
                                 const maxVal = currency === "USD"
                                    ? d.income.threeHigherIncomeCourses[0].totalEarningsUSD
                                    : d.income.threeHigherIncomeCourses[0].totalEarningsARS;
                                 return (
                                    <Box key={i}>
                                       <Box py="sm">
                                          <Flex justify="space-between" mb={6}>
                                             <Text size="sm" fw={500} c={INK} lineClamp={1} style={{ maxWidth: "70%" }}>{c._id}</Text>
                                             <Text size="sm" fw={700} c={GREEN_SOFT}>{fmt(val, currency)}</Text>
                                          </Flex>
                                          <Progress value={maxVal ? (val / maxVal) * 100 : 0} size={3} radius="xl" bg={SURFACE} color="teal" />
                                       </Box>
                                       {i < d.income.threeHigherIncomeCourses.length - 1 && <RowDivider />}
                                    </Box>
                                 );
                              })}
                           </Stack>
                        </Paper>
                     </Grid.Col>

                     {/* Menor ingreso */}
                     <Grid.Col span={{ base: 12, md: 6 }}>
                        <Paper p="lg" radius="md" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
                           <SectionTitle>Menor ingreso por curso</SectionTitle>
                           <Stack gap={0}>
                              {(!d?.income?.threeLowerIncomeCourses || d?.income?.threeHigherIncomeCourses?.length === 0) && <Text size="sm">N No se han encontrado resultados para este rango de fechas del <strong>{moment(dateFilter[0]).format('DD-MM-YYYY')}</strong> al <strong>{moment(dateFilter[1]).format('DD-MM-YYYY')}</strong></Text>}

                              {d.income.threeLowerIncomeCourses?.map((c, i) => {
                                 const val = c.totalEarningsUSD > 0 ? c.totalEarningsUSD : c.totalEarningsARS;
                                 const cur = c.totalEarningsUSD > 0 ? "USD" : "ARS";
                                 return (
                                    <Box key={i}>
                                       <Flex justify="space-between" align="center" py="sm">
                                          <Text size="sm" fw={500} c={INK} lineClamp={1} style={{ maxWidth: "70%" }}>{c._id}</Text>
                                          <Group gap={6}>
                                             <Text size="10px" c={MUTED} tt="uppercase" fw={600} style={{ letterSpacing: 0.5 }}>{cur}</Text>
                                             <Text size="sm" fw={600} c={RED_SOFT}>{fmt(val, cur)}</Text>
                                          </Group>
                                       </Flex>
                                       {i < d.income.threeLowerIncomeCourses.length - 1 && <RowDivider />}
                                    </Box>
                                 );
                              })}
                           </Stack>
                        </Paper>
                     </Grid.Col>

                  </Grid>
               </Tabs.Panel>

               {/* ══ CURSOS ══ */}
               <Tabs.Panel value="cursos">
                  <Grid gutter="md">

                     <Grid.Col span={{ base: 12, md: 6 }}>
                        <Paper p="lg" radius="md" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
                           <SectionTitle>Más populares</SectionTitle>
                           <Stack gap={0}>
                              {d.courses.threeMostPopularCourses.map((c, i) => (
                                 <Box key={i}>
                                    <Box py="sm">
                                       <Flex justify="space-between" align="flex-start" mb={6}>
                                          <Box style={{ maxWidth: "75%" }}>
                                             <Text size="sm" fw={600} c={INK} lineClamp={1}>{c.comercialInformation.name}</Text>
                                             <Group gap={6} mt={4}>
                                                <TypeBadge type={c.type} />
                                                <Text size="xs" c={MUTED}>{formatCourseStartDate(c)}</Text>
                                             </Group>
                                          </Box>
                                          <Box ta="right">
                                             <Text size="md" fw={700} c={INK}>{c.studentsCount}</Text>
                                             <Text size="10px" c={MUTED} tt="uppercase" style={{ letterSpacing: 0.5 }}>alumnos</Text>
                                          </Box>
                                       </Flex>
                                       <Progress
                                          value={(c.studentsCount / d.courses.threeMostPopularCourses[0].studentsCount) * 100}
                                          size={3} radius="xl" bg={SURFACE} color="teal"
                                       />
                                    </Box>
                                    {i < d.courses.threeMostPopularCourses.length - 1 && <RowDivider />}
                                 </Box>
                              ))}
                           </Stack>
                        </Paper>
                     </Grid.Col>

                     <Grid.Col span={{ base: 12, md: 6 }}>
                        <Paper p="lg" radius="md" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
                           <SectionTitle>Menor asistencia</SectionTitle>
                           <Stack gap={0}>
                              {d.courses.threeLeastAttendedCourses.map((c, i) => (
                                 <Box key={i}>
                                    <Flex justify="space-between" align="flex-start" py="sm">
                                       <Box style={{ maxWidth: "75%" }}>
                                          <Text size="sm" fw={600} c={INK} lineClamp={1}>{c.comercialInformation.name}</Text>
                                          <Group gap={6} mt={4}>
                                             <TypeBadge type={c.type} />
                                             <Text size="xs" c={MUTED}>{formatCourseStartDate(c)}</Text>
                                          </Group>
                                       </Box>
                                       <Box ta="right">
                                          <Text size="md" fw={700} c={RED_SOFT}>{c.studentsCount}</Text>
                                          <Text size="10px" c={MUTED} tt="uppercase" style={{ letterSpacing: 0.5 }}>alumnos</Text>
                                       </Box>
                                    </Flex>
                                    {i < d.courses.threeLeastAttendedCourses.length - 1 && <RowDivider />}
                                 </Box>
                              ))}
                           </Stack>
                        </Paper>
                     </Grid.Col>

                     <Grid.Col span={12}>
                        <Paper p="lg" radius="md" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
                           <SectionTitle>Comparativa de popularidad</SectionTitle>
                           <ResponsiveContainer width="100%" height={200}>
                              <BarChart data={barData} barCategoryGap="30%">
                                 <XAxis dataKey="name" tick={{ fill: MUTED, fontSize: 10 }} axisLine={false} tickLine={false} />
                                 <YAxis tick={{ fill: MUTED, fontSize: 10 }} axisLine={false} tickLine={false} />
                                 <Tooltip
                                    contentStyle={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                                    labelStyle={{ color: INK, fontWeight: 600, fontSize: 12 }}
                                    itemStyle={{ color: SUBTEXT }}
                                    formatter={v => [v, "Alumnos"]}
                                 />
                                 <Bar dataKey="alumnos" radius={[3, 3, 0, 0]}>
                                    {barData.map((e, i) => <Cell key={i} fill={e.kind === "top" ? TEAL : "#c0392b33"} stroke={e.kind === "top" ? "none" : RED_SOFT} strokeWidth={e.kind === "top" ? 0 : 1} />)}
                                 </Bar>
                              </BarChart>
                           </ResponsiveContainer>
                           <Flex gap="lg" justify="center" mt="xs">
                              <Group gap={6}>
                                 <Box style={{ width: 10, height: 3, background: TEAL, borderRadius: 2 }} />
                                 <Text size="xs" c={MUTED}>Más populares</Text>
                              </Group>
                              <Group gap={6}>
                                 <Box style={{ width: 10, height: 3, background: RED_SOFT, borderRadius: 2 }} />
                                 <Text size="xs" c={MUTED}>Menor asistencia</Text>
                              </Group>
                           </Flex>
                        </Paper>
                     </Grid.Col>

                  </Grid>
               </Tabs.Panel>

               {/* ══ INICIO DEL MES ══ */}
               <Tabs.Panel value="inicio">
                  <Paper p="lg" radius="md" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
                     <Flex justify="space-between" align="center" mb="md">
                        <SectionTitle>Unidades que inician en estas fechas</SectionTitle>
                        <Text size="xs" c={MUTED} fw={600}>{d.courses.initNow.length} unidades</Text>
                     </Flex>
                     <Stack gap={0}>
                        {d.courses.initNow.length === 0 ? (
                           <Center py="xl">
                              <Text c={MUTED} size="sm">No hay unidades con inicio estas fechas.</Text>
                           </Center>
                        ) : d.courses.initNow.map((c, i) => {
                           const price = c.comercialInformation.price;
                           const priceStr = price?.USD ? fmt(price.USD, "USD") : price?.ARS ? fmt(price.ARS, "ARS") : "—";
                           const isOpen = c.state?.toLowerCase() === "open";
                           return (
                              <Box key={i}>
                                 <Flex justify="space-between" align="center" py="sm">
                                    <Group gap="md">
                                       <Box style={{
                                          width: 34, height: 34, borderRadius: 8,
                                          background: TEAL_BG,
                                          display: "flex", alignItems: "center", justifyContent: "center",
                                          flexShrink: 0,
                                       }}>
                                          <Text size="xs" fw={700} c={TEAL}>{c.type[0]}</Text>
                                       </Box>
                                       <Box>
                                          <Text size="sm" fw={600} c={INK}>{c.comercialInformation.name}</Text>
                                          <Group gap={8} mt={3}>
                                             <TypeBadge type={c.type} />
                                             <Text size="xs" c={MUTED}>{formatCourseStartDate(c)}</Text>
                                          </Group>
                                       </Box>
                                    </Group>
                                    <Box ta="right" style={{ flexShrink: 0 }}>
                                       <Text size="sm" fw={700} c={INK}>{priceStr}</Text>
                                       <Text
                                          size="10px"
                                          c={isOpen ? GREEN_SOFT : MUTED}
                                          tt="uppercase"
                                          fw={600}
                                          mt={2}
                                          style={{ letterSpacing: 0.5 }}
                                       >
                                          {isOpen ? "abierta" : "cerrada"}
                                       </Text>
                                    </Box>
                                 </Flex>
                                 {i < d.courses.initNow.length - 1 && <RowDivider />}
                              </Box>
                           );
                        })}
                     </Stack>
                  </Paper>
               </Tabs.Panel>

            </Tabs>
         </Box>
      </>
   );
}